import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { Icon } from '../components/Icon';
import { SurahCard } from '../components/SurahCard';
import { surahIndex, surahs } from '../data/quran';
import { usePageMeta } from '../lib/seo';

export function HomePage() {
  usePageMeta({
    title: "Juz 'Amma leren — Arabisch, Nederlands & audio | juzamma.nl",
    description: "Leer de 37 soera's van Juz 'Amma lezen en begrijpen met Nederlandse vertaling, transliteratie, audio en quizzen.",
    path: '/',
  });
  const [query, setQuery] = useState('');
  const startSurah = surahs[0];
  const filteredSurahs = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase('nl');
    if (!normalized) return surahs;
    return surahs.filter((surah) => [surah.number, surah.latinName, surah.meaning].some((value) => String(value).toLocaleLowerCase('nl').includes(normalized)));
  }, [query]);

  return (
    <>
      <section className="hero pattern-section">
        <div className="container hero-grid">
          <div className="hero-copy">
            <h1>Juz ‘Amma leren</h1>
            <p>Lees mee in het Arabisch, luister per vers en oefen tot de woorden vertrouwd voelen. Met Nederlandse vertaling voor ouders, kinderen en iedereen die begint.</p>
            <div className="hero-actions">
              <Link className="button button-primary button-large" to={startSurah.path}>Begin met lezen <Icon name="arrow" /></Link>
              <a className="button button-secondary button-large" href="#alle-soeras">Bekijk 37 soera’s</a>
            </div>
          </div>
          <div className="hero-visual" aria-label="Voorbeeld van Arabische Korantekst met vertaling">
            <div className="quran-card">
              <div className="quran-card-top"><span>Soera 112</span><span>الإخلاص</span></div>
              <p dir="rtl" lang="ar">{surahIndex.featuredAyah.arabic}</p>
              <p>{surahIndex.featuredAyah.translation}</p>
              <div className="sound-wave" aria-hidden="true">{[18, 31, 23, 42, 27, 35, 19, 39, 25, 31, 17, 28].map((height, index) => <i key={index} style={{ height }} />)}</div>
            </div>
          </div>
        </div>
      </section>

      <section className="surah-overview container" id="alle-soeras" aria-labelledby="soeras-titel">
        <div className="section-heading split-heading">
          <div><span className="eyebrow">Juz 30 · soera 114–78</span><h2 id="soeras-titel">Alle soera’s van Juz ‘Amma</h2><p>Begin bij An-Nas en werk van de korte soera’s terug naar An-Naba.</p></div>
          <label className="search-field"><Icon name="search" /><span className="sr-only">Zoek een soera</span><input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Zoek op naam of nummer…" /></label>
        </div>
        <div className="surah-grid">
          {filteredSurahs.map((surah) => <SurahCard key={surah.number} surah={surah} />)}
        </div>
        {!filteredSurahs.length && <p className="empty-state">Geen soera gevonden. Probeer een andere naam of een nummer tussen 78 en 114.</p>}
      </section>

      <section className="learn-section pattern-section">
        <div className="container reading-content">
          <div className="section-heading"><span className="eyebrow">Voor ouders en beginners</span><h2>Waarom beginnen met Juz ‘Amma?</h2></div>
          <div className="prose-grid">
            <div>
              <p>Juz ‘Amma is het dertigste en laatste deel van de Koran. Het begint bij soera An-Naba en eindigt bij soera An-Nas. Veel soera’s in dit deel zijn kort. Daarom horen kinderen ze vaak tijdens het gebed en zijn ze geschikt om het leren rustig op te bouwen.</p>
              <p>Voor een kind is herkenning belangrijk: eerst vaak luisteren, daarna kleine stukken nazeggen en pas later zonder hulp reciteren. De Nederlandse vertaling helpt ouders om samen te praten over wat een vers betekent, zonder de Arabische tekst te vervangen.</p>
            </div>
            <aside className="tip-card">
              <span>Een eenvoudig weekritme</span>
              <ol>
                <li><strong>Luister</strong><p>Speel één vers drie tot vijf keer af.</p></li>
                <li><strong>Zeg na</strong><p>Oefen in korte stukjes, zonder haast.</p></li>
                <li><strong>Herhaal samen</strong><p>Koppel het nieuwe vers aan het vorige.</p></li>
                <li><strong>Gebruik het gebed</strong><p>Reciteer wat al stevig voelt.</p></li>
              </ol>
            </aside>
          </div>
        </div>
      </section>

      <section className="container tips-section" aria-labelledby="tips-titel">
        <div className="section-heading center-heading"><span className="eyebrow">Klein houden werkt</span><h2 id="tips-titel">Samen oefenen zonder druk</h2><p>Vijf aandachtige minuten zijn vaak waardevoller dan een lang oefenmoment met tegenzin.</p></div>
        <div className="tips-grid">
          <article><span>01</span><h3>Eén vers per keer</h3><p>Luister, lees mee en herhaal. Voeg pas een volgend vers toe wanneer het eerste vertrouwd klinkt.</p></article>
          <article><span>02</span><h3>Vaste momenten</h3><p>Koppel oefenen aan een herkenbaar moment, bijvoorbeeld na het ontbijt of voor het slapen.</p></article>
          <article><span>03</span><h3>Herhalen hoort erbij</h3><p>Een vergeten stukje is geen mislukking. Teruggaan en opnieuw luisteren hoort bij het leerproces.</p></article>
        </div>
      </section>

      <section className="cta-section">
        <div className="container cta-inner"><div><span className="eyebrow">Klaar voor een kleine stap?</span><h2>Kies één soera en begin met luisteren.</h2></div><Link className="button button-cream button-large" to={startSurah.path}>Naar {startSurah.latinName} <Icon name="arrow" /></Link></div>
      </section>
    </>
  );
}
